import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Truck,
  MapPin,
  Package,
  Search,
  Plus,
  Minus,
  X,
  Loader2,
  Gift,
  Clock,
  Scale,
  Euro,
  Info,
  Ban,
  Zap,
  Layers,
} from 'lucide-react'
import {
  shippingRates,
  ShippingOption,
  ShippingRatesProduct,
  ShippingRatesZone,
  ShippingRatesOverview,
} from '@/lib/api'
import { useDebounce } from '@/hooks/useDebounce'

// ============================================================================
// Helpers
// ============================================================================

const eur = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' })
const kg = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 2 })

// Nombre corto de cada tipo de producto (prefijo de la referencia)
const PRODUCT_TYPES: Record<string, string> = {
  PANT: 'Pantallas',
  COV: 'Carcasas',
  TEC: 'Teclados',
  VENT: 'Ventiladores',
  BAT: 'Baterías',
  JACK: 'Conectores de carga',
  REAC: 'Reacondicionados',
  CARG: 'Cargadores',
  BIS: 'Bisagras',
  FLEX: 'Cables flex',
}

interface CarrierStyle {
  short: string
  tile: string
}

function carrierStyle(name: string): CarrierStyle {
  const n = name.toLowerCase()
  if (n.includes('mrw')) return { short: 'MRW', tile: 'bg-red-600 text-white' }
  if (n.includes('correos express') || n.includes('cex'))
    return { short: 'CEX', tile: 'bg-yellow-400 text-blue-900' }
  if (n.includes('gls')) return { short: 'GLS', tile: 'bg-blue-900 text-yellow-300' }
  if (n.includes('fedex')) return { short: 'FedEx', tile: 'bg-purple-800 text-orange-400' }
  return { short: name.replace(/[^a-zA-Z]/g, '').slice(0, 3).toUpperCase(), tile: 'bg-slate-600 text-white' }
}

// "GLS España - Entrega a domicilio" → "GLS España" + "Entrega a domicilio"
function splitCarrierName(name: string): { title: string; detail: string | null } {
  const [title, ...rest] = name.split(' - ')
  return { title: title.trim(), detail: rest.length ? rest.join(' - ').trim() : null }
}

// Nombre corto para listas: "GLS España", "MRW Express"; si el nombre no dice el
// destino y el transportista solo cubre una zona fuera de España, se añade.
function carrierLabel(carrierId: number, name: string, overview: ShippingRatesOverview): string {
  const { title } = splitCarrierName(name)
  const zones = overview.zones.filter((z) => z.carriers.some((c) => c.carrier_id === carrierId))
  if (zones.length === 1 && !zones[0].is_spain && title.split(' ').length === 1) {
    return `${title} ${zones[0].countries[0] || zones[0].zone_name}`
  }
  return title
}

function rangeLabel(min: number, max: number): string {
  if (max >= 1000) return `Más de ${kg.format(min)} kg`
  if (min <= 0) return `Hasta ${kg.format(max)} kg`
  return `${kg.format(min)} – ${kg.format(max)} kg`
}

function parseDecimal(value: string): number | null {
  const n = parseFloat(value.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

// ============================================================================
// Pequeños componentes
// ============================================================================

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/80 dark:border-slate-700 shadow-sm ${className}`}
    >
      {children}
    </div>
  )
}

function CardTitle({ icon: Icon, title, hint }: { icon: any; title: string; hint?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-3">
      <div className="flex items-center gap-2.5">
        <Icon className="w-[18px] h-[18px] text-primary-600 dark:text-primary-400" />
        <h2 className="text-[15px] font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
      </div>
      {hint}
    </div>
  )
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">
      {children}
    </label>
  )
}

const inputClass =
  'w-full h-11 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 ' +
  'text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 ' +
  'focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 transition'

function PlusVat({ size = 'md' }: { size?: 'sm' | 'md' }) {
  return (
    <span
      className={`inline-flex items-center rounded-md font-semibold tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300 ${
        size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'
      }`}
    >
      + IVA
    </span>
  )
}

function CarrierTile({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' }) {
  const style = carrierStyle(name)
  return (
    <div
      className={`shrink-0 flex items-center justify-center rounded-xl font-black tracking-tight ${style.tile} ${
        size === 'sm' ? 'w-9 h-9 text-[11px]' : 'w-12 h-12 text-sm'
      }`}
    >
      {style.short}
    </div>
  )
}

// ============================================================================
// Resultado
// ============================================================================

function ResultRow({ option, cheapest }: { option: ShippingOption; cheapest: boolean }) {
  const { title, detail } = splitCarrierName(option.carrier_name)
  return (
    <div
      className={`flex items-center gap-4 px-4 py-3.5 rounded-xl border transition ${
        !option.available
          ? 'border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40'
          : cheapest
            ? 'border-primary-300 dark:border-primary-500/50 bg-primary-50/60 dark:bg-primary-500/10 ring-1 ring-primary-200 dark:ring-primary-500/20'
            : 'border-slate-200 dark:border-slate-700'
      }`}
    >
      <div className={option.available ? '' : 'opacity-40 grayscale'}>
        <CarrierTile name={option.carrier_name} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`font-semibold ${
              option.available ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'
            }`}
          >
            {title}
          </span>
          {cheapest && option.available && (
            <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide bg-primary-600 text-white">
              Más económico
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          {detail && <span className="truncate">{detail}</span>}
          {!option.available ? (
            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
              <Clock className="w-3 h-3" />
              {option.inactive_until ? `No se ofrece ahora · vuelve a las ${option.inactive_until}` : 'No se ofrece ahora'}
            </span>
          ) : (
            option.same_day_available && (
              <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                <Zap className="w-3 h-3" />
                Sale hoy
              </span>
            )
          )}
        </div>
      </div>

      <div className="text-right shrink-0">
        {option.free_shipping ? (
          <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400">Gratis</span>
        ) : (
          <div className="flex items-baseline justify-end gap-2">
            <span
              className={`text-2xl font-bold tabular-nums tracking-tight ${
                option.available ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-500'
              }`}
            >
              {eur.format(option.price_net)}
            </span>
            <PlusVat />
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Tabla de tarifas
// ============================================================================

function ZoneRatesTable({ zone, overview }: { zone: ShippingRatesZone; overview: ShippingRatesOverview }) {
  const carriers = zone.carriers.map((c) => ({
    ...c,
    info: overview.carriers.find((o) => o.carrier_id === c.carrier_id),
  }))
  // Tramos de peso: los de cualquiera de los transportistas de la zona
  const ranges = useMemo(() => {
    const map = new Map<string, { min: number; max: number }>()
    zone.carriers.forEach((c) =>
      c.ranges.forEach((r) => map.set(`${r.weight_min}-${r.weight_max}`, { min: r.weight_min, max: r.weight_max }))
    )
    return [...map.values()].sort((a, b) => a.min - b.min)
  }, [zone])

  return (
    <div className="overflow-x-auto -mx-5 px-5">
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="text-left font-medium text-slate-500 dark:text-slate-400 py-2.5 pr-4 whitespace-nowrap">
              <span className="inline-flex items-center gap-1.5">
                <Scale className="w-3.5 h-3.5" /> Peso del paquete
              </span>
            </th>
            {carriers.map((c) => (
              <th key={c.carrier_id} className="py-2.5 px-3 text-right font-medium">
                <div className="inline-flex items-center gap-2 justify-end">
                  <CarrierTile name={c.info?.carrier_name || ''} size="sm" />
                  <span className="text-slate-700 dark:text-slate-200 whitespace-nowrap">
                    {splitCarrierName(c.info?.carrier_name || '').title}
                  </span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ranges.map((r) => {
            const prices = carriers.map(
              (c) => c.ranges.find((x) => x.weight_min === r.min && x.weight_max === r.max)?.price_net
            )
            const available = prices.filter((p): p is number => p !== undefined)
            const best = available.length > 1 ? Math.min(...available) : undefined
            return (
              <tr key={`${r.min}-${r.max}`} className="border-t border-slate-100 dark:border-slate-700/70">
                <td className="py-2.5 pr-4 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {rangeLabel(r.min, r.max)}
                </td>
                {prices.map((p, i) => (
                  <td key={i} className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap">
                    {p === undefined ? (
                      <span className="text-slate-300 dark:text-slate-600">—</span>
                    ) : (
                      <span
                        className={
                          p === best
                            ? 'font-semibold text-primary-700 dark:text-primary-300'
                            : 'text-slate-800 dark:text-slate-200'
                        }
                      >
                        {eur.format(p)}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function EuropeRatesTable({ zones, overview }: { zones: ShippingRatesZone[]; overview: ShippingRatesOverview }) {
  const ranges = useMemo(() => {
    const map = new Map<string, { min: number; max: number }>()
    zones.forEach((z) =>
      z.carriers.forEach((c) =>
        c.ranges.forEach((r) => map.set(`${r.weight_min}-${r.weight_max}`, { min: r.weight_min, max: r.weight_max }))
      )
    )
    const all = [...map.values()].sort((a, b) => a.min - b.min)
    // Si ningún país cambia de precio según el peso, una sola columna
    const flat = zones.every((z) => new Set(z.carriers.flatMap((c) => c.ranges.map((r) => r.price_net))).size <= 1)
    return flat && all.length > 1 ? [{ min: all[0].min, max: all[all.length - 1].max, flat: true }] : all
  }, [zones])
  const priceGroups = useMemo(() => {
    const groups = new Map<number, string[]>()
    zones.forEach((z) => {
      const price = Math.min(...z.carriers.flatMap((c) => c.ranges.map((r) => r.price_net)))
      groups.set(price, [...(groups.get(price) || []), ...(z.countries.length ? z.countries : [z.zone_name])])
    })
    return [...groups.entries()].sort((a, b) => a[0] - b[0]).map(([p, n]) => [p, n.sort()] as [number, string[]])
  }, [zones])
  const carrierNames = [
    ...new Set(
      zones.flatMap((z) =>
        z.carriers.map((c) => overview.carriers.find((o) => o.carrier_id === c.carrier_id)?.carrier_name || '')
      )
    ),
  ]

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 text-xs text-slate-500 dark:text-slate-400">
        {carrierNames.map((n) => (
          <span key={n} className="inline-flex items-center gap-2">
            <CarrierTile name={n} size="sm" />
            <span>{n}</span>
          </span>
        ))}
      </div>
      {ranges.length === 1 && 'flat' in ranges[0] ? (
        // Mismo precio para cualquier peso: países agrupados por precio
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {priceGroups.map(([price, names]) => (
            <div
              key={price}
              className="flex items-start gap-4 px-4 py-3.5 rounded-xl border border-slate-200 dark:border-slate-700"
            >
              <div className="shrink-0 text-lg font-bold tabular-nums text-slate-900 dark:text-white w-24">
                {eur.format(price)}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{names.join(', ')}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left font-medium text-slate-500 dark:text-slate-400 py-2.5 pr-4">Países</th>
                {ranges.map((r) => (
                  <th
                    key={`${r.min}-${r.max}`}
                    className="py-2.5 px-3 text-right font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap"
                  >
                    {rangeLabel(r.min, r.max)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {zones.map((z) => (
                <tr key={z.zone_id} className="border-t border-slate-100 dark:border-slate-700/70">
                  <td className="py-2.5 pr-4 text-slate-800 dark:text-slate-200">
                    {z.countries.length ? z.countries.join(', ') : z.zone_name}
                  </td>
                  {ranges.map((r) => {
                    const p = z.carriers
                      .flatMap((c) => c.ranges)
                      .filter((x) => x.weight_min === r.min && x.weight_max === r.max)
                      .map((x) => x.price_net)
                    return (
                      <td
                        key={`${r.min}-${r.max}`}
                        className="py-2.5 px-3 text-right tabular-nums text-slate-800 dark:text-slate-200 whitespace-nowrap"
                      >
                        {p.length ? eur.format(Math.min(...p)) : <span className="text-slate-300 dark:text-slate-600">—</span>}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Página
// ============================================================================

interface CartLine extends ShippingRatesProduct {
  quantity: number
}

export default function ShippingRatesPage() {
  // Destino
  const [country, setCountry] = useState('ES')
  const [postalCode, setPostalCode] = useState('')

  // Productos
  const [search, setSearch] = useState('')
  const [showResults, setShowResults] = useState(false)
  const [lines, setLines] = useState<CartLine[]>([])
  const searchBoxRef = useRef<HTMLDivElement>(null)

  // Peso e importe: se calculan de los productos, pero se pueden escribir a mano
  const [weightInput, setWeightInput] = useState('0,5')
  const [totalInput, setTotalInput] = useState('')

  // Zona seleccionada en la tabla de tarifas
  const [tableZoneId, setTableZoneId] = useState<number | 'europe' | null>(null)

  const overviewQuery = useQuery({
    queryKey: ['shipping-rates', 'overview'],
    queryFn: shippingRates.overview,
    staleTime: 60_000,
  })
  const overview = overviewQuery.data

  const debouncedSearch = useDebounce(search.trim(), 250)
  const productsQuery = useQuery({
    queryKey: ['shipping-rates', 'products', debouncedSearch],
    queryFn: () => shippingRates.searchProducts(debouncedSearch),
    enabled: debouncedSearch.length >= 2,
  })

  // Al cambiar los productos, recalcular peso e importe
  useEffect(() => {
    if (lines.length === 0) return
    const w = lines.reduce((s, l) => s + (l.weight || 0.1) * l.quantity, 0)
    const t = lines.reduce((s, l) => s + l.price_with_tax * l.quantity, 0)
    setWeightInput(kg.format(Math.max(w, 0.1)))
    setTotalInput(t.toFixed(2).replace('.', ','))
  }, [lines])

  // Cerrar el desplegable al pulsar fuera
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) setShowResults(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const weight = parseDecimal(weightInput) ?? 0
  const orderTotal = parseDecimal(totalInput)
  const needsPostalCode = country === 'ES'
  const destinationReady = !needsPostalCode || /^\d{5}$/.test(postalCode.trim())

  const quoteParams = useDebounce(
    {
      postal_code: postalCode.trim(),
      country,
      weight: Math.max(weight, 0.01),
      references: lines.map((l) => l.reference),
      order_total: orderTotal ?? undefined,
    },
    350
  )
  const quoteQuery = useQuery({
    queryKey: ['shipping-rates', 'quote', quoteParams],
    queryFn: () => shippingRates.quote(quoteParams),
    enabled:
      destinationReady &&
      weight > 0 &&
      (quoteParams.country !== 'ES' || /^\d{5}$/.test(quoteParams.postal_code)),
    placeholderData: (prev) => prev,
  })
  const quote = destinationReady ? quoteQuery.data : undefined

  const cheapestId = useMemo(() => {
    const available = quote?.options.filter((o) => o.available) || []
    if (available.length < 2 || quote?.free_shipping) return null
    return available.reduce((a, b) => (b.price_net < a.price_net ? b : a)).carrier_id
  }, [quote])

  const sortedOptions = useMemo(
    () =>
      [...(quote?.options || [])].sort(
        (a, b) => Number(b.available) - Number(a.available) || a.price_net - b.price_net
      ),
    [quote]
  )

  // Tabla: por defecto la zona del destino calculado
  const domesticZones = overview?.zones.filter((z) => z.is_spain || z.countries.includes('Portugal')) || []
  const europeZones = overview?.zones.filter((z) => !domesticZones.includes(z)) || []
  const activeTableZone =
    tableZoneId ?? (quote?.zone_id && domesticZones.some((z) => z.zone_id === quote.zone_id) ? quote.zone_id : null) ??
    domesticZones[0]?.zone_id ?? null
  const selectedZone = domesticZones.find((z) => z.zone_id === activeTableZone)

  const addProduct = (p: ShippingRatesProduct) => {
    setLines((prev) => {
      const i = prev.findIndex((l) => l.reference === p.reference)
      if (i >= 0) return prev.map((l, j) => (j === i ? { ...l, quantity: l.quantity + 1 } : l))
      return [...prev, { ...p, quantity: 1 }]
    })
    setSearch('')
    setShowResults(false)
  }
  const changeQty = (ref: string, delta: number) =>
    setLines((prev) =>
      prev.map((l) => (l.reference === ref ? { ...l, quantity: l.quantity + delta } : l)).filter((l) => l.quantity > 0)
    )
  const clearProducts = () => {
    setLines([])
    setWeightInput('0,5')
    setTotalInput('')
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center shadow-lg shadow-primary-500/20">
            <Truck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Portes</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Lo que paga el cliente por el envío, igual que en la tienda online.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-amber-800 dark:text-amber-300 text-sm">
          <Info className="w-4 h-4 shrink-0" />
          <span>
            Todos los importes son <strong>netos</strong>: al cliente se le suma el <strong>IVA</strong>.
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ============================ Calculadora ============================ */}
        <Card className="lg:col-span-2 self-start">
          <CardTitle icon={MapPin} title="Destino y pedido" />
          <div className="px-5 pb-5 space-y-5">
            <div className="grid grid-cols-5 gap-3">
              <div className="col-span-3">
                <Label>País</Label>
                <select
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value)
                    setTableZoneId(null)
                  }}
                  className={inputClass}
                >
                  {(overview?.countries || [{ iso: 'ES', name: 'España' }]).map((c) => (
                    <option key={c.iso} value={c.iso}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <Label>Código postal</Label>
                <input
                  value={postalCode}
                  onChange={(e) => {
                    setPostalCode(e.target.value)
                    setTableZoneId(null)
                  }}
                  placeholder={needsPostalCode ? '28001' : 'Opcional'}
                  inputMode="numeric"
                  className={`${inputClass} tabular-nums`}
                />
              </div>
            </div>

            {/* Productos */}
            <div>
              <div className="flex items-center justify-between">
                <Label>Productos (opcional)</Label>
                {lines.length > 0 && (
                  <button
                    onClick={clearProducts}
                    className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 mb-1.5"
                  >
                    Vaciar
                  </button>
                )}
              </div>
              <div ref={searchBoxRef} className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    setShowResults(true)
                  }}
                  onFocus={() => setShowResults(true)}
                  placeholder="Referencia o nombre: PANT-062, teclado…"
                  className={`${inputClass} pl-10`}
                />
                {productsQuery.isFetching && (
                  <Loader2 className="w-4 h-4 text-slate-400 absolute right-3.5 top-1/2 -translate-y-1/2 animate-spin" />
                )}
                {showResults && debouncedSearch.length >= 2 && productsQuery.data && (
                  <div className="absolute z-20 mt-1.5 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden">
                    {productsQuery.data.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-slate-500">Sin resultados</div>
                    ) : (
                      <ul className="max-h-72 overflow-y-auto py-1">
                        {productsQuery.data.map((p) => (
                          <li key={p.reference}>
                            <button
                              onClick={() => addProduct(p)}
                              className="w-full text-left px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700/60 flex items-center gap-3"
                            >
                              <Plus className="w-4 h-4 text-primary-500 shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-mono font-semibold text-slate-700 dark:text-slate-200">
                                    {p.reference}
                                  </span>
                                  {!p.active && (
                                    <span className="text-[10px] px-1 rounded bg-slate-100 dark:bg-slate-700 text-slate-500">
                                      inactivo
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-slate-600 dark:text-slate-300 truncate">{p.name}</div>
                              </div>
                              <span className="text-xs text-slate-400 tabular-nums shrink-0">
                                {kg.format(p.weight)} kg
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {lines.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {lines.map((l) => (
                    <li
                      key={l.reference}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-100 dark:border-slate-700"
                    >
                      <Package className="w-4 h-4 text-slate-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-mono font-semibold text-slate-700 dark:text-slate-200">
                          {l.reference}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{l.name}</div>
                      </div>
                      <div className="flex items-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800">
                        <button
                          onClick={() => changeQty(l.reference, -1)}
                          className="w-7 h-7 flex items-center justify-center text-slate-500 hover:text-slate-800 dark:hover:text-white"
                          aria-label="Quitar uno"
                        >
                          {l.quantity === 1 ? <X className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                        </button>
                        <span className="w-6 text-center text-sm font-semibold tabular-nums text-slate-800 dark:text-slate-100">
                          {l.quantity}
                        </span>
                        <button
                          onClick={() => changeQty(l.reference, 1)}
                          className="w-7 h-7 flex items-center justify-center text-slate-500 hover:text-slate-800 dark:hover:text-white"
                          aria-label="Añadir uno"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Peso total</Label>
                <div className="relative">
                  <input
                    value={weightInput}
                    onChange={(e) => setWeightInput(e.target.value)}
                    inputMode="decimal"
                    className={`${inputClass} pr-10 tabular-nums`}
                  />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">kg</span>
                </div>
              </div>
              <div>
                <Label>Importe pedido (IVA incl.)</Label>
                <div className="relative">
                  <input
                    value={totalInput}
                    onChange={(e) => setTotalInput(e.target.value)}
                    placeholder="—"
                    inputMode="decimal"
                    className={`${inputClass} pr-8 tabular-nums`}
                  />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">€</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500 -mt-2">
              Con productos, el peso y el importe se rellenan solos. El importe solo se usa para ver si el envío sale
              gratis.
            </p>
          </div>
        </Card>

        {/* ============================ Resultado ============================ */}
        <Card className="lg:col-span-3 self-start">
          <CardTitle
            icon={Euro}
            title="Porte para el cliente"
            hint={
              quote?.zone_name && quote.deliverable ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-700/70 text-xs font-medium text-slate-600 dark:text-slate-300">
                  <MapPin className="w-3 h-3" />
                  {quote.zone_name}
                </span>
              ) : null
            }
          />
          <div className="px-5 pb-5">
            {!destinationReady ? (
              <div className="flex flex-col items-center justify-center text-center py-14">
                <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-700/60 flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-slate-400" />
                </div>
                <p className="font-medium text-slate-700 dark:text-slate-200">Escribe el código postal del cliente</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Verás al momento lo que cuesta el envío con cada transportista.
                </p>
              </div>
            ) : quoteQuery.isError ? (
              <div className="py-10 text-center text-sm text-red-600 dark:text-red-400">
                No se ha podido calcular el porte.{' '}
                {(quoteQuery.error as any)?.response?.data?.detail || ''}
              </div>
            ) : !quote ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
              </div>
            ) : !quote.deliverable ? (
              <div className="flex flex-col items-center justify-center text-center py-12">
                <div className="w-14 h-14 rounded-2xl bg-red-50 dark:bg-red-500/10 flex items-center justify-center mb-4">
                  <Ban className="w-6 h-6 text-red-500" />
                </div>
                <p className="font-medium text-slate-800 dark:text-slate-100">No se envía a este destino</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {quote.zone_name ? `${quote.zone_name}: ` : ''}la tienda no tiene transportista para esta zona.
                </p>
              </div>
            ) : (
              <div className={`space-y-3 transition-opacity ${quoteQuery.isFetching ? 'opacity-60' : ''}`}>
                {quote.free_shipping && (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20">
                    <Gift className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                    <p className="text-sm text-emerald-800 dark:text-emerald-300">
                      <strong>Envío gratis</strong>: el pedido supera{' '}
                      {eur.format(quote.free_shipping_threshold || 0)} con IVA.
                    </p>
                  </div>
                )}

                {sortedOptions.map((o) => (
                  <ResultRow key={o.carrier_id} option={o} cheapest={o.carrier_id === cheapestId} />
                ))}

                {!quote.free_shipping && quote.missing_for_free != null && quote.free_shipping_threshold && (
                  <div className="pt-2">
                    <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-1.5">
                      <span className="inline-flex items-center gap-1.5">
                        <Gift className="w-3.5 h-3.5" />
                        Faltan <strong className="text-slate-700 dark:text-slate-200">{eur.format(quote.missing_for_free)}</strong>{' '}
                        para envío gratis
                      </span>
                      <span className="tabular-nums">{eur.format(quote.free_shipping_threshold)}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-emerald-500"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.max(0, (1 - quote.missing_for_free / quote.free_shipping_threshold) * 100)
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* ============================ Reglas ============================ */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <Card className="p-5">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center">
                <Gift className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h3 className="font-semibold text-slate-800 dark:text-slate-100">Envío gratis</h3>
            </div>
            {overview.free_shipping_threshold ? (
              <>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Pedidos desde{' '}
                  <strong className="text-slate-900 dark:text-white">
                    {eur.format(overview.free_shipping_threshold)}
                  </strong>{' '}
                  <span className="text-slate-500">(IVA incluido)</span>.
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">
                  Solo en España peninsular y Portugal peninsular. Baleares y el resto de países pagan porte siempre.
                </p>
              </>
            ) : (
              <p className="text-sm text-slate-500">No hay envío gratis por importe.</p>
            )}
          </Card>

          <Card className="p-5">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center">
                <Layers className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="font-semibold text-slate-800 dark:text-slate-100">Recargo por producto</h3>
            </div>
            {overview.surcharges.length === 0 ? (
              <p className="text-sm text-slate-600 dark:text-slate-300">Ahora mismo ningún producto lleva recargo.</p>
            ) : (
              <>
                <ul className="space-y-1.5">
                  {overview.surcharges.map((s) => (
                    <li key={`${s.prefix}-${s.amount_net}`} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-300">
                        {PRODUCT_TYPES[s.prefix] || s.prefix}
                        <span className="text-xs text-slate-400 ml-1.5">{s.products} fichas</span>
                      </span>
                      <span className="inline-flex items-center gap-1.5 font-semibold tabular-nums text-slate-900 dark:text-white">
                        +{eur.format(s.amount_net)} <PlusVat size="sm" />
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2.5 leading-relaxed">
                  Se suma al porte el recargo más alto del pedido, una sola vez.
                </p>
              </>
            )}
          </Card>

          <Card className="p-5">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center">
                <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="font-semibold text-slate-800 dark:text-slate-100">Horarios</h3>
            </div>
            <ul className="space-y-2">
              {overview.carriers.map((c) => (
                <li key={c.carrier_id} className="flex items-center gap-2.5 text-sm">
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${c.available ? 'bg-emerald-500' : 'bg-amber-400'}`}
                  />
                  <span className="text-slate-700 dark:text-slate-200 truncate flex-1" title={c.carrier_name}>
                    {carrierLabel(c.carrier_id, c.carrier_name, overview)}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap tabular-nums">
                    {c.inactive_window
                      ? `no se ofrece ${c.inactive_window.replace('-', '–')}`
                      : 'todo el día'}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2.5">Fines de semana: todos disponibles.</p>
          </Card>
        </div>
      )}

      {/* ============================ Tarifas ============================ */}
      <Card className="mt-6">
        <CardTitle
          icon={Truck}
          title="Tarifas por zona"
          hint={
            <span className="inline-flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              Precios netos por envío <PlusVat size="sm" />
            </span>
          }
        />
        <div className="px-5 pb-5">
          {overviewQuery.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
            </div>
          ) : overviewQuery.isError || !overview ? (
            <div className="py-10 text-center text-sm text-red-600 dark:text-red-400">
              No se han podido cargar las tarifas.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-4">
                {domesticZones.map((z) => (
                  <button
                    key={z.zone_id}
                    onClick={() => setTableZoneId(z.zone_id)}
                    className={`shrink-0 px-3.5 py-2 rounded-xl text-sm font-medium border transition ${
                      activeTableZone === z.zone_id
                        ? 'bg-primary-600 border-primary-600 text-white shadow-sm shadow-primary-600/30'
                        : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-500'
                    }`}
                  >
                    {z.zone_name}
                  </button>
                ))}
                {europeZones.length > 0 && (
                  <button
                    onClick={() => setTableZoneId('europe')}
                    className={`shrink-0 px-3.5 py-2 rounded-xl text-sm font-medium border transition ${
                      activeTableZone === 'europe'
                        ? 'bg-primary-600 border-primary-600 text-white shadow-sm shadow-primary-600/30'
                        : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-500'
                    }`}
                  >
                    Resto de Europa
                  </button>
                )}
              </div>

              {activeTableZone === 'europe' ? (
                <EuropeRatesTable zones={europeZones} overview={overview} />
              ) : selectedZone ? (
                <>
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    {selectedZone.free_shipping && overview.free_shipping_threshold && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                        <Gift className="w-3 h-3" />
                        Gratis desde {eur.format(overview.free_shipping_threshold)} con IVA
                      </span>
                    )}
                    <span className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                      {(selectedZone.provinces.length ? selectedZone.provinces : selectedZone.countries).join(' · ')}
                    </span>
                  </div>
                  <ZoneRatesTable zone={selectedZone} overview={overview} />
                </>
              ) : null}
            </>
          )}
        </div>
      </Card>
    </div>
  )
}
